// PowerSync local-first dispatch for `createSpace`.
//
// Flow:
//   1. Generate a UUID v4 client-side for the space.
//   2. Read room_type + floor from the local PowerSync mirror so the
//      response DTO carries the joined names without a server round-trip.
//   3. Compute a placeholder serial_number locally (max existing + 1
//      for the same (building, room_type)) so the displayName looks
//      right immediately. The server picks the authoritative serial
//      under SELECT FOR UPDATE; the sync stream overwrites the local
//      row when the authoritative version arrives.
//   4. INSERT the space row locally with the placeholder serial.
//   5. Return SpaceWithItems with items=[] — the server materialises
//      the room-type template (1 asset / space rows) inside its
//      transaction; those items arrive via the sync stream within the
//      next sync cycle. UI consumers using reactive useQuery() pick
//      them up automatically.
//   6. Emit a `space-created` event with the optimistic shape.
//
// The connector picks up the SDK CRUD entry and POSTs to
// `/capture/sessions/:id/spaces` with the same client-supplied id.
// Backend honours the id (idempotent retry on same id; the server's
// usual unique-on-(building, room_type, serial) constraint protects
// against same-(building, room_type, serial) collisions but doesn't
// fire on different-id same-scope racers — serial is auto-assigned
// per-row and naturally diverges).
//
// Trade-off vs. createItem / createFieldAddedVariant: those mutations
// have a clear conflict scope so the backend can return 409
// already_exists with an authoritative payload. createSpace doesn't —
// concurrent space creations on the same (building, room_type) just
// pick different serials and both succeed. So the connector path is
// simpler: POST → success → emit; on 5xx → retry; no 409 swallow.

import type {
  CaptureMutation,
  CaptureMutationResult,
} from './capture-client.types'
import { eventForResult } from './capture-client-events'
import type { CaptureEventBus } from './capture-events'

import type { PowerSyncDb } from './capture-client-powersync'

/** Wrapped for testability: tests inject a fixed UUID generator so
 * assertions against the local row's id are deterministic. The
 * runtime path uses crypto.randomUUID() directly. */
export function generateSpaceUuid(): string {
  return crypto.randomUUID()
}

/** Normalise an optional surveyor-supplied alias to the value the
 * optimistic local row should carry (FFR2): trim, then treat a blank or
 * whitespace-only string — and an omitted (undefined) / explicit null —
 * as null. Mirrors the capture service's server-side rule so the local
 * mirror and the authoritative server row converge byte-for-byte. */
export function normaliseOptimisticAlias(
  alias: string | null | undefined,
): string | null {
  if (alias === undefined || alias === null) return null
  const trimmed = alias.trim()
  return trimmed.length === 0 ? null : trimmed
}

interface SessionLookupRow {
  siteId: string
  buildingId: string | null
}

interface RoomTypeLookupRow {
  name: string
}

interface FloorLookupRow {
  name: string
}

export async function dispatchCreateSpace(
  mutation: Extract<CaptureMutation, { kind: 'createSpace' }>,
  db: PowerSyncDb,
  bus: CaptureEventBus,
): Promise<CaptureMutationResult> {
  const id = generateSpaceUuid()
  const nowIso = new Date().toISOString()

  // 1. Look up session info so we can populate building_id on the
  // local row. The capture session already exists in the local mirror
  // (the user is already in a capture session by the time they tap
  // "+ Add room"), so this is a synchronous read.
  const sessionRows = await db.getAll<SessionLookupRow>(
    `SELECT site_id AS siteId, building_id AS buildingId
     FROM capture_sessions
     WHERE id = ?
     LIMIT 1`,
    [mutation.sessionId],
  )
  const session = sessionRows[0]
  if (!session) {
    throw new Error(
      `dispatchCreateSpace: capture session ${mutation.sessionId} not found in local PowerSync mirror — ` +
        `the user must have an active session before creating a room (this is a routing bug, not a data race).`,
    )
  }
  if (session.buildingId === null || session.buildingId === undefined) {
    throw new Error(
      `dispatchCreateSpace: capture session ${mutation.sessionId} is site-scoped (buildingId is null); rooms can only be created on building-scoped sessions.`,
    )
  }

  // 2. Look up the room type and floor names so the response DTO is
  // immediately usable by the UI. PowerSync streams these into the
  // local mirror as part of `reference_global` / `reference_site_scoped`,
  // so they're guaranteed present once the user has navigated into
  // capture for this site.
  const roomTypeRows = await db.getAll<RoomTypeLookupRow>(
    `SELECT name FROM room_types WHERE id = ? LIMIT 1`,
    [mutation.roomTypeId],
  )
  const roomTypeName = roomTypeRows[0]?.name ?? ''

  const floorRows = await db.getAll<FloorLookupRow>(
    `SELECT name FROM floors WHERE id = ? LIMIT 1`,
    [mutation.floorId],
  )
  const floorName = floorRows[0]?.name ?? null

  // 3. Compute placeholder serial: max existing serial for same
  // (building, room_type) + 1. The server's SELECT FOR UPDATE
  // serialises authoritative allocation; this local value is provisional
  // and the sync stream overwrites it. When two clients race to create
  // an Office on the same building, both pick local serial=N+1, then
  // server picks N+1 and N+2 in some order — the sync stream delivers
  // the correct values back to each client.
  const serialRows = await db.getAll<{ maxSerial: number | null }>(
    `SELECT MAX(serial_number) AS maxSerial
     FROM spaces
     WHERE building_id = ? AND room_type_id = ?`,
    [session.buildingId, mutation.roomTypeId],
  )
  const placeholderSerial = (serialRows[0]?.maxSerial ?? 0) + 1
  const displayName = `${roomTypeName} ${placeholderSerial}`.trim()

  // 4. Optimistic INSERT.
  //
  // `created_in_session_id` is the per-row ownership signal cancel-
  // session uses to scope its orphan-room cleanup (Codex P2 on PR
  // #548). Stamping the dispatching session id keeps the local
  // optimistic row byte-compatible with the authoritative server row
  // that arrives via the sync stream, so the next cancel run on this
  // session prunes only rows this session owns.
  // Optional surveyor-supplied alias (FFR2). Normalise blank/whitespace
  // to null locally so the optimistic row matches the value the backend
  // will persist (the capture service applies the same trim + empty→null
  // rule). `mutation.alias` is undefined when the surveyor left the
  // field empty.
  const alias = normaliseOptimisticAlias(mutation.alias)

  await db.execute(
    `INSERT INTO spaces (
       id,
       building_id,
       floor_id,
       room_type_id,
       serial_number,
       display_name,
       alias,
       is_pseudo,
       is_field_added,
       requires_review,
       created_at,
       updated_at,
       created_in_session_id
     ) VALUES (?, ?, ?, ?, ?, ?, ?, 0, 0, 0, ?, ?, ?)`,
    [
      id,
      session.buildingId,
      mutation.floorId,
      mutation.roomTypeId,
      placeholderSerial,
      displayName,
      alias,
      nowIso,
      nowIso,
      mutation.sessionId,
    ],
  )

  // 5. Assemble the response. Items=[] — the server materialises the
  // room-type template (1 asset / space rows) inside the create-space
  // transaction; those rows arrive via the sync stream within the
  // next sync cycle. The UI uses reactive useQuery() bindings on
  // capture_items so the room view re-renders as items arrive.
  const space = {
    id,
    floorId: mutation.floorId,
    floorName,
    roomTypeId: mutation.roomTypeId,
    roomTypeName,
    serialNumber: placeholderSerial,
    displayName,
    // Optional surveyor-supplied alias (FFR2). The same normalised value
    // written to the local row above, so the optimistic SpaceWithItems
    // matches both the local mirror and the authoritative server row
    // that later arrives over the sync stream. Null when the surveyor
    // left the field empty.
    alias,
    isPseudo: false,
    isFieldAdded: false,
    requiresReview: false,
  }

  const result: CaptureMutationResult = {
    kind: 'createSpace',
    space,
    items: [],
  }
  bus.emit(eventForResult(mutation, result))
  return result
}
